import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { myApi } from "../../../config/axiosConfig";
import Cookies from "js-cookie";

const LOCATIONS = [
    { value: "COLOMBIA", label: "Colombia" },
    { value: "ARGUINDEGUI", label: "Arguindegui" }
];

const emptyForm = {
    location: "COLOMBIA",
    date: dayjs().format("YYYY-MM-DDTHH:mm"),
    quantity: "",
    amount: "",
    iva: "",
    supplier: "",
    invoice: "",
    comments: ""
};

const DefPurchaseModal = ({
    open,
    onClose,
    carga,
    isEditing,
    defaultLocation,
    tankLevel,
    tankCapacity,
    onCreate,
    onUpdate
}) => {
    const fixedLocation = carga?.location || defaultLocation || "COLOMBIA";

    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && carga) {
            setForm({
                location: fixedLocation,
                date: carga.date
                    ? dayjs(carga.date).format("YYYY-MM-DDTHH:mm")
                    : dayjs().format("YYYY-MM-DDTHH:mm"),
                quantity:
                    fixedLocation === "ARGUINDEGUI"
                        ? carga.gallons || ""
                        : carga.liters || "",
                amount: carga.subtotal || "",
                iva: carga.iva || "",
                supplier: carga.supplier || "",
                invoice: carga.invoice || "",
                comments: carga.comments || ""
            });
        } else if (open) {
            setForm({
                ...emptyForm,
                location: fixedLocation
            });
        }
    }, [open, carga, fixedLocation]);

    const toNumber = (value) => {
        if (value === "" || value === null || value === undefined) return 0;
        return Number(String(value).replace(/,/g, "")) || 0;
    };

    const isArguin = fixedLocation === "ARGUINDEGUI";
    const unitLabel = isArguin ? "GAL" : "L";

    const safeTankLevel = Math.max(Number(tankLevel) || 0, 0);
    const safeTankCapacity = Math.max(Number(tankCapacity) || 0, 0);
    const safeAvailableSpace = Math.max(safeTankCapacity - safeTankLevel, 0);

    const quantity = toNumber(form.quantity);
    const amount = toNumber(form.amount);
    const iva = isArguin ? 0 : toNumber(form.iva);
    const total = isArguin ? amount : amount + iva;

    const unitPrice = quantity > 0 ? total / quantity : 0;

    const handleChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validate = () => {
        if (!fixedLocation) return "Selecciona una ubicación.";
        if (!form.date) return "Selecciona la fecha.";
        if (quantity <= 0) return "Captura cantidad.";
        if (amount <= 0) return "Captura monto.";
        if (!isArguin && form.iva === "") return "Captura IVA.";
        if (!isArguin && iva < 0) return "Captura un IVA válido.";

        if (
            !isArguin &&
            !isEditing &&
            quantity > safeAvailableSpace
        ) {
            return `La compra excede la capacidad del tanque. Disponible: ${safeAvailableSpace.toLocaleString()} ${unitLabel}`;
        }

        return null;
    };

    const handleSave = async () => {
        const error = validate();

        if (error) {
            Swal.fire({
                target: document.getElementById("dialogDefPurchase"),
                icon: "warning",
                title: error
            });
            return;
        }

        setSaving(true);

        try {
            const payload = {
                location: fixedLocation,
                date: dayjs(form.date).format("YYYY-MM-DD HH:mm:ss"),
                liters: isArguin ? 0 : quantity,
                gallons: isArguin ? quantity : 0,
                subtotal: amount,
                iva,
                supplier: form.supplier || "",
                invoice: form.invoice || "",
                comments: form.comments || "",
                usuario: Cookies.get("ebt_nombre")
            };

            const response = isEditing
                ? await myApi.put(`/cargasFuel/updateDefPurchase/${carga.id}`, payload)
                : await myApi.post("/cargasFuel/createDefPurchase", payload);

            if (response.data.success) {
                if (isEditing) {
                    await onUpdate(response.data.data);
                } else {
                    await onCreate(response.data.data);
                }

                Swal.fire({
                    target: document.getElementById("dialogDefPurchase"),
                    icon: "success",
                    title: isEditing ? "Compra actualizada" : "Compra guardada"
                });

                onClose();
            } else {
                Swal.fire({
                    target: document.getElementById("dialogDefPurchase"),
                    icon: "error",
                    title: "Error",
                    text: response.data.message || "No se pudo guardar"
                });
            }
        } catch (error) {
            console.error("Error save DEF purchase:", error);

            Swal.fire({
                target: document.getElementById("dialogDefPurchase"),
                icon: "error",
                title: "Error al guardar"
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            id="dialogDefPurchase"
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle fontWeight={800}>
                {isEditing ? "Editar compra DEF" : "Agregar compra DEF"}
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Box>
                            <Box sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                                Ubicación
                            </Box>

                            <Box sx={{ fontSize: 18, fontWeight: 700 }}>
                                {LOCATIONS.find(item => item.value === fixedLocation)?.label || fixedLocation}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Fecha"
                            type="datetime-local"
                            value={form.date}
                            onChange={(e) => handleChange("date", e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label={isArguin ? "Galones comprados" : "Litros comprados"}
                            type="number"
                            value={form.quantity}
                            onChange={(e) => handleChange("quantity", e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label={isArguin ? "Monto USD" : "Monto MXN sin IVA"}
                            type="number"
                            value={form.amount}
                            onChange={(e) => handleChange("amount", e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {!isArguin && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="IVA MXN"
                                type="number"
                                value={form.iva}
                                onChange={(e) => handleChange("iva", e.target.value)}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {!isArguin && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ fontSize: 13, color: "text.secondary" }}>
                                Capacidad: <strong>{safeTankCapacity.toLocaleString()} {unitLabel}</strong>
                                <br />
                                Nivel actual: <strong>{safeTankLevel.toLocaleString()} {unitLabel}</strong>
                                <br />
                                Disponible: <strong>{safeAvailableSpace.toLocaleString()} {unitLabel}</strong>
                            </Box>
                        </Grid>
                    )}

                    {isEditing && (
                        <>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Proveedor"
                                    value={form.supplier}
                                    onChange={(e) => handleChange("supplier", e.target.value)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Factura / Orden"
                                    value={form.invoice}
                                    onChange={(e) => handleChange("invoice", e.target.value)}
                                    fullWidth
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    label="Comentarios"
                                    value={form.comments}
                                    onChange={(e) => handleChange("comments", e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid size={{ xs: 12 }}>
                        <Box
                            sx={{
                                p: 2,
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                bgcolor: "#f7f7f7",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1
                            }}
                        >
                            {!isArguin && (
                                <>
                                    <Box>
                                        IVA:
                                        <strong>
                                            {" "}
                                            ${iva.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </strong>
                                    </Box>

                                    <Box>
                                        Total con IVA:
                                        <strong>
                                            {" "}
                                            ${total.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </strong>
                                    </Box>

                                    <Box>
                                        Precio por litro:
                                        <strong>
                                            {" "}
                                            ${unitPrice.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </strong>
                                    </Box>
                                </>
                            )}

                            {isArguin && (
                                <>
                                    <Box>
                                        Precio por galón:
                                        <strong>
                                            {" "}
                                            ${unitPrice.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </strong>
                                    </Box>

                                    <Box>
                                        Total USD:
                                        <strong>
                                            {" "}
                                            ${total.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </strong>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Cancelar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ bgcolor: "black", color: "white" }}
                >
                    {saving ? "Guardando..." : "Guardar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DefPurchaseModal;